<script setup>
import {useManagementStore} from "@/stores/management.js";
import {computed, onMounted, ref} from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {gregorianDate} from "@/utils/gregorianDate.js";
import AllButtons from "@/views/orders/Shared/AllButtons.vue";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import {Icon} from "@iconify/vue";
import AddStore from "@/views/management/AddStore.vue";
import UpdateStore from "@/views/management/UpdateStore.vue";
import {useUserStore} from "@/stores/user.js";

const managementStore = useManagementStore();
const userStore = useUserStore();
const searchQuery = ref();
const isShowAddModal = ref(false);
const isShowUpdateModal = ref(false);
const isLoading = ref(false);
const selectedStore = ref(null)

const stores = computed(() => {
  return managementStore.getStores.map(store => ({
    ...store,
    commissionFee: store.commissionFee || store.commissionFee === 0
        ? Number(store.commissionFee)
        : null,
    withdrawalFee: store.withdrawalFee || store.withdrawalFee === 0
        ? Number(store.withdrawalFee)
        : null
  }))
});

async function getStores() {
  isLoading.value = true;
  await managementStore.action_getStores();
  isLoading.value = false;
}

function openAddModal() {
  isShowAddModal.value = true
}

function closeAddModal() {
  isShowAddModal.value = false
}

function openUpdateModal({data}) {
  if (userStore.hasPermission('BIG_BOSS') || userStore.hasPermission('STORES_MANAGER_UPDATE')) {
    selectedStore.value = data
    isShowUpdateModal.value = true
  }
}

function closeUpdateModal() {
  isShowUpdateModal.value = false
}

function refreshList() {
  closeAddModal();
  closeUpdateModal();
  managementStore.action_getStores();
}

onMounted(async () => {
  await getStores();
})
</script>

<template>
  <div class="h-full">
    <div class="flex flex-col gap-4 w-full h-full">
      <div class="flex flex-row justify-between gap-4">
        <Breadcrumbs/>
        <AllButtons :buttonForNewItem="true" titleForNewItem="New Store" :showFilter="false"
                    :showCategoriesButtons="false" v-model="searchQuery" :copyButton="false"
                    @buttonForNewItemClicked="openAddModal" permissionForNewItem="STORES_MANAGER_UPDATE"/>
      </div>
      <div v-if="isLoading" class="flex items-center justify-center h-60">
        <Icon icon="eos-icons:bubble-loading" class="size-12"/>
      </div>
      <DataTable
          v-else
          :nullSortOrder="-1"
          :value="stores"
          scrollable
          scrollHeight="flex"
          :virtualScrollerOptions="{ itemSize: 30 }"
          resizableColumns
          removableSort
          stripedRows
          class="text-xs w-full"
          size="small"
          tableStyle="width: 100%; table-layout: fixed;"
          row-hover
          @row-click="openUpdateModal"
          :rowClass="() => 'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300 cursor-pointer'"
      >
        <Column field="store" header="Store" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
                style="width: 10%; min-width: 100px; height: 30px">
        </Column>
        <Column field="origin" header="Origin" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap"
                style="width: 10%; min-width: 100px; height: 30px"></Column>
        <Column field="currency" header="Currency" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap"
                style="width: 10%; min-width: 100px; height: 30px"></Column>
        <Column field="commissionFee" header="Commission Fee" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap"
                style="width: 10%; min-width: 100px; height: 30px">
          <template #body="slotProps">
            <span class="block w-full max-w-max">
              {{ Number((slotProps.data.commissionFee * 100).toFixed(2)) }} %
            </span>
          </template>
        </Column>
        <Column field="withdrawalFee" header="Withdrawal Fee" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap"
                style="width: 10%; min-width: 100px; height: 30px">
          <template #body="slotProps">
            <span class="block w-full max-w-max">
              {{ Number((slotProps.data.withdrawalFee * 100).toFixed(2)) }} %
            </span>
          </template>
        </Column>
        <Column field="lastUpdate" header="Last Update" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap"
                style="width: 11%; min-width: 120px; height: 30px">
          <template #body="slotProps">
            <span class="block w-full max-w-max">
              {{ gregorianDate(slotProps.data.lastUpdate, 'DD MMM HH:mm') }}
            </span>
          </template>
        </Column>
        <template #empty>
          <h1 class="py-2 text-center text-lg">No Data</h1>
        </template>
      </DataTable>
    </div>
    <update-store v-if="isShowUpdateModal" :store="selectedStore" @closeModal="closeUpdateModal"
                  @deletedStore="refreshList" @updatedStore="refreshList"/>
    <add-store v-if="isShowAddModal" @closeModal="closeAddModal" @addedNewStore="refreshList"/>
  </div>
</template>

<style scoped lang="scss">
.card {
  width: 100%;
  margin: 0 !important;
  padding: 0 !important;
}

:deep(.p-datatable) {
  margin: 0 !important;
  padding: 0 !important;
  scrollbar-width: thin;
  scrollbar-color: #4b5563 #1f2937;
}

:deep(.p-datatable-wrapper) {
  margin-top: 0 !important;
}

:deep(.p-datatable-table) {
  /*height: 100% !important;*/
  width: 100% !important;
  table-layout: fixed;
  border-collapse: collapse;
}

:deep(.p-datatable-thead) {
  position: sticky;
  top: 0;
  z-index: 20;
  background-color: #1a2941;
}

:deep(.p-datatable-thead th) {
  font-size: 0.75rem;
  white-space: nowrap;
  padding: 0.5rem 0.5rem 0.5rem 1rem;
  text-transform: capitalize;
  text-align: left;
  background-color: #1a2941 !important;
  border-top: 0;
}

:deep(.p-datatable-thead th.p-datatable-column-sorted) {
  background-color: #1E444F !important;
}

:deep(.p-datatable-column-sorted) {
  background-color: #1e444f !important;
}

:deep(.p-datatable-tbody > tr > td) {
  font-size: 0.75rem;
  text-align: left;
  padding: 0.3rem;
  text-transform: none;
}

:deep(.p-datatable::-webkit-scrollbar) {
  width: 8px;
}

:deep(.p-datatable::-webkit-scrollbar-track) {
  background: #1f2937;
}

:deep(.p-datatable::-webkit-scrollbar-thumb) {
  background: #4b5563;
  border-radius: 4px;
}
</style>